"use client";

import { cn } from "@/lib/utils";
import { FC, useMemo, useState, useEffect, useRef } from "react";
import { useIsInView } from "@/hooks/useIsInView";
import * as z from "@/lib/propSchema";
import Image from "next/image";
import { LeftArrow, RightArrow } from "@/assets/Arrow";
import { ProgressSlider } from "@/components/atoms/ProgressSlider";
import { formatBackgroundStyle } from "@/utils/styles/format";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "@/constants/leadConstants";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { leadCtaClick } from "@/customAnalytics/trackingEvents";
import RevealOnScroll from "@/components/atoms/RevealOnScroll";
import { shadowFromColor } from "@/utils/utils";

const CertificateSchema = z.object({
    id: z.union([z.number(), z.string()]),
    meta_image: z.string(),
    meta_alt_image: z.string().optional().default(""),
    meta_title: z.string(),
    meta_title_color: z.string().optional().default(""),
    meta_priority: z.number().optional().default(0),
});

const Certificates1DataSchema = z.object({
    desktop_background_color: z.string().optional().default(""),
    mobile_background_color: z.string().optional().default(""),
    backvariant: z.enum(["variant_1", "variant_2", "variant_3"]).optional().default("variant_3"),
    layout_variant: z.union([z.string()]).optional().default("variant_1"),
    gradient_color_1: z.string().optional().default(""),
    gradient_color_2: z.string().optional().default(""),
    gradient_color_3: z.string().optional().default(""),
    gradient_color_4: z.string().optional().default(""),
    pre_title: z.string().optional().default("Earn Multiple"),
    pre_title_color: z.string().optional().default(""),
    title: z.string().optional().default("Certifications"),
    title_start_color: z.string().optional().default(""),
    title_end_color: z.string().optional().default(""),
    title_gradient_direction: z.string().optional().default(""),
    apply_now_text: z.string().optional().default(""),
    apply_now_button_start_color: z.string().optional().default(""),
    apply_now_button_end_color: z.string().optional().default(""),
    button_gradient_direction: z.string().optional().default(""),
    enable_button_shadow: z.union([z.boolean(), z.number()]).optional().default(false),
    apply_now_button_icon: z.string().nullish().default(""),
    apply_now_button_icon_alt: z.string().nullish().default(""),
    left_navigation_icon: z.string().optional(),
    alt_text_left_nav_icon: z.string().optional().default(""),
    right_navigation_icon: z.string().optional(),
    alt_text_right_nav_icon: z.string().optional().default(""),
    /* in milliseconds (default 3000ms when missing or invalid) */
    scroll_frequency: z.coerce.number().optional().default(0).transform((v) => (v > 0 ? v : 0)),
    enable_animation: z.union([z.boolean(), z.number()]).optional().default(false),
    metadata: z.array(CertificateSchema).optional().default([]),
});

type Certificate = z.infer<typeof CertificateSchema>;

// Certificate Card Component
const CertificateCard: FC<{
    certificate: Certificate;
    position: number;
    isMobile: boolean;
    isV2: boolean;
}> = ({ certificate, position, isMobile, isV2 }) => {
    
    const getCardStyle = () => {
        if (isMobile) {
            // Mobile: Simple horizontal translation
            return {
                transform: `translateX(${position * 100}%)`,
                opacity: position === 0 ? 1 : 0,
                zIndex: position === 0 ? 10 : 0,
            };
        }
        
        // Desktop: Stacked cards with prev 35% left, next 35% right
        if (position === 0) {
            // Current/Active card - centered on top
            return {
                transform: 'scale(1) translateX(0%) translateY(0)',
                filter: 'blur(0px)',
                zIndex: 30,
                opacity: 1,
            };
        } else if (position === -1) {
            // Previous card - 35% to the left, scaled down, behind
            return {
                transform: 'scale(0.65) translateX(-45%) translateY(20px)',
                filter: 'blur(4px)',
                zIndex: 20,
                opacity: 0.8,
            };
        } else if (position === 1) {
            // Next card - 35% to the right, scaled down, behind
            return {
                transform: 'scale(0.65) translateX(45%) translateY(20px)',
                filter: 'blur(4px)',
                zIndex: 20,
                opacity: 0.8,
            };
        } else {
            // Hidden cards - completely hidden
            return {
                transform: 'scale(0.5) translateY(60px)',
                filter: 'blur(10px)',
                zIndex: 0,
                opacity: 0,
            };
        }
    };

    return (
        <div
            className={cn(
                "absolute transition-all duration-500 ease-in-out",
                "flex flex-col items-center w-full"
            )}
            style={getCardStyle()}
        >
            {/* Certificate Image */}
            <div 
                className={cn(
                    "relative overflow-hidden mx-auto",
                    "rounded-[8px]",
                    // xs (Mobile) - 354px x 255px
                    "w-[327px] h-[230px]",
                    // sm (1080px) - 420px x 303px
                    "sm:w-[420px] sm:h-[303px] sm:rounded-[10px]",
                    // md (1230px) - 469px x 338px
                    "md:w-[469px] md:h-[338px] md:rounded-[11px]",
                    // lg (1430px) - 562px x 405px
                    "lg:w-[562px] lg:h-[405px] lg:rounded-[13px]",
                    // xl (1750px) - 648px x 467px
                    "xl:w-[648px] xl:h-[467px] xl:rounded-[15px]"
                )}
            >
                <Image
                    src={certificate.meta_image}
                    alt={certificate.meta_alt_image || certificate.meta_title}
                    fill
                    className="object-cover"
                    priority={position === 0}
                />
            </div>

            {position === 0 && (
                <p
                    className={cn(
                        "text-center italic mx-auto w-full",
                        // Gap between image and title
                        "mt-[16px] sm:mt-[24px] md:mt-[24px] lg:mt-[32px] xl:mt-[32px]",
                        isV2
                            ? cn(
                                // Figma: Poppins Medium Italic 14px (mobile) → Merriweather Bold
                                // Italic (web, from sm); single line on web. Kept compact (18 → 20px)
                                // so the certificate name doesn't overpower the card.
                                "font-poppins font-medium sm:font-merriweather sm:font-bold",
                                "text-[14px] sm:text-[18px] lg:text-[20px] tracking-[0.14px] sm:tracking-[0.18px] leading-[1.4]",
                                "max-w-[354px] sm:max-w-none sm:whitespace-nowrap",
                              )
                            : cn(
                                "font-bold",
                                "max-w-[354px] sm:max-w-[203px] md:max-w-[271px]",
                                "xs:text-[17px] sm:text-[18px] md:text-[20px]",
                              ),
                    )}
                    style={{
                        ...(isV2 ? {} : { fontFamily: "var(--font-merriweather)" }),
                        color: certificate.meta_title_color
                    }}
                >
                    {certificate.meta_title}
                </p>
            )}
        </div>
    );
};

export const Certificates1: FC<any> = (props) => {
    const actualData = props?.data?.data || props?.data || props;
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isMobile, setIsMobile] = useState(false);
    const [touchStart, setTouchStart] = useState<number | null>(null);
    const [touchEnd, setTouchEnd] = useState<number | null>(null);
    const [isPaused, setIsPaused] = useState(false);

    const parsedResult = useMemo(() => Certificates1DataSchema.safeParse(actualData), [actualData]);
    
    const parsedData = useMemo(() => parsedResult.success ? parsedResult.data : null, [parsedResult]);
    const certificates = useMemo(() => 
        parsedData ? [...parsedData.metadata].sort((a, b) => a.meta_priority - b.meta_priority) : [],
        [parsedData]
    );

    const dispatch = useDispatch();

    const ms = parsedData?.scroll_frequency || 0;

    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const sectionRef = useRef<HTMLElement>(null);
    const inView = useIsInView(sectionRef);

    // Detect mobile/desktop
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 1080); // sm breakpoint
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    /*
      Auto-scroll carousel based on scroll_frequency (schema ensures valid ms, default 3000).

      `inView` is part of the guard on purpose. Without it this interval runs from mount
      until unmount no matter where the section sits, and authors do configure it low —
      `online-mba` has `scroll_frequency: "1000"`, i.e. one React commit per second
      re-rendering the whole carousel for a section far below the fold.
    */
    useEffect(() => {
        if (certificates.length <= 1 || isPaused || ms <= 0 || !inView) {
            return;
        }

        const interval = setInterval(() => {
            setCurrentIndex((prev) => {
                if (prev >= certificates.length - 1) {
                    return 0;
                }
                return prev + 1;
            });
        }, ms);

        return () => clearInterval(interval);
    }, [parsedData?.scroll_frequency, certificates.length, isPaused, ms, inView]);

    if (!parsedResult.success || !parsedData) {
        console.error("validation failed", parsedResult.error);
        return null;
    }

    if (certificates.length === 0) {
        return null;
    }

    const handlePrev = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        
        setCurrentIndex((prev) => Math.max(0, prev - 1));
        // Pause auto-scroll temporarily when user manually navigates
        setIsPaused(true);
        timeoutRef.current = setTimeout(() => setIsPaused(false), 3000); // Resume after 5 seconds
    };

    const handleNext = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        
        setCurrentIndex((prev) => Math.min(certificates.length - 1, prev + 1));
        // Pause auto-scroll temporarily when user manually navigates
        setIsPaused(true);
        timeoutRef.current = setTimeout(() => setIsPaused(false), 3000); // Resume after 5 seconds
    };

    // Touch swipe handlers
    const minSwipeDistance = 50; // Minimum distance for a swipe

    const onTouchStart = (e: React.TouchEvent) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e: React.TouchEvent) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe) {
            handleNext();
        } else if (isRightSwipe) {
            handlePrev();
        }
    };

    const bgColor = formatBackgroundStyle(parsedData, isMobile, "#ffffff");

    const isAnimationEnabled =
        parsedData.enable_animation === true || parsedData.enable_animation === 1;

    // variant_2 = Figma "Typography System" redesign (Poppins, rounded CTA, mobile CTA).
    const isV2 = parsedData.layout_variant === "variant_2";

    const handleApplyClick = () => {
        leadCtaClick({
            section: "certificates_section",
            component: "cta_button",
            label: parsedData.apply_now_text,
        });
        dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.REGISTER }));
    };

    // Shared CTA visuals (colour driven by the CMS gradient + optional drop-shadow).
    const ctaGradient =
        parsedData.button_gradient_direction &&
        parsedData.apply_now_button_start_color &&
        parsedData.apply_now_button_end_color
            ? `linear-gradient(${parsedData.button_gradient_direction}, ${parsedData.apply_now_button_start_color}, ${parsedData.apply_now_button_end_color})`
            : undefined;
    const ctaShadow = parsedData.enable_button_shadow
        ? `drop-shadow(0px 10px 10px ${shadowFromColor(
              parsedData.apply_now_button_start_color ||
                  parsedData.apply_now_button_end_color ||
                  "#9f1c34"
          )})`
        : undefined;
    // v2 title is a solid Poppins maroon (not the v1 gradient); honour a CMS colour if set.
    const v2TitleColor =
        parsedData.title_start_color &&
        parsedData.title_start_color.toUpperCase() !== "#FFFFFF"
            ? parsedData.title_start_color
            : "#9f1c34";

    return (
        <section
            ref={sectionRef}
            className={cn(
                "relative w-full overflow-hidden flex justify-center",
                "px-[24px] py-[40px]",
                "sm:py-[40px]",
                "md:py-[60px]",
                "lg:py-[60px]",
                "xl:py-[80px]",
                // Figma component reference: v2 uses a flat 80px horizontal padding from sm up
                // (mobile keeps its 24px). v1 retains its original per-breakpoint gutters.
                isV2
                    ? "sm:px-[80px]"
                    : "sm:px-[60px] md:px-[60px] lg:px-[80px] xl:px-[140px]"
            )}
            style={bgColor}
        >
            <div className={cn(
                "w-full flex",
                "flex-col gap-[24px]",
                "sm:flex-row sm:gap-[40px] sm:items-start sm:justify-between",
                "md:gap-[60px]",
                "lg:gap-[80px]",
                "xl:gap-[100px]",
                "max-w-[354px]",
                "sm:max-w-[960px]",
                "md:max-w-[1110px]",
                "lg:max-w-[1270px]",
                "xl:max-w-[1470px]"
            )}>
                {/* Left Column - Text Content */}
                <div className={cn(
                    "flex flex-col shrink-0",
                    "w-full",
                    "sm:w-[381px] sm:mt-[45px]",
                    "md:w-[481px] md:mt-[20px]",
                    "lg:w-[525px] lg:mt-[68px]",
                    "xl:w-[525px] xl:mt-[68px]"
                )}>
                    {/* pre_title + title are one <h2>. Spans because a heading may only hold
                        phrasing content; the flex column blockifies them. */}
                    <h2 className={cn(
                        "relative flex flex-col",
                        // Figma v2: title + subtitle centered on mobile, left-aligned from sm.
                        isV2 && "text-center sm:text-left",
                        "mb-[16px]",
                        "sm:mb-[20px]",
                        "lg:mb-[24px]"
                    )}>
                        <span
                            className={cn(
                                "line-clamp-2",
                                isV2
                                    ? // Figma: Poppins Medium 24px (mobile) → 36px (web, from sm).
                                      "font-poppins font-medium leading-[1.25] text-[24px] sm:text-[36px] tracking-[-0.36px] sm:tracking-[-0.54px]"
                                    : "font-bold leading-[1.2] text-[24px] sm:text-[28px] md:text-[30px]",
                            )}
                            style={{
                                // v2 family resolves via the font-poppins class; only v1 needs inline.
                                fontFamily: isV2 ? undefined : "var(--font-google-sans)",
                                color: parsedData.pre_title_color || "rgba(34, 34, 34, 0.8)",
                            }}
                        >
                            {parsedData.pre_title}
                        </span>
                        {isV2 ? (
                            <span
                                className="font-poppins font-semibold line-clamp-3 leading-[1.15] text-[28px] sm:text-[48px] tracking-[-0.56px] sm:tracking-[-0.96px]"
                                style={{ color: v2TitleColor }}
                            >
                                {parsedData.title}
                            </span>
                        ) : (
                        <span className="gradient-title-denton-blue line-clamp-3"
                            style={{
                                backgroundImage: parsedData.title_start_color && parsedData.title_end_color ?
                                        `linear-gradient(${parsedData.title_gradient_direction}, ${parsedData.title_start_color}, ${parsedData.title_end_color})` :
                                        undefined,
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                            }}
                        >
                            {parsedData.title}
                        </span>
                        )}
                    </h2>

                    {parsedData.apply_now_text && (
                        <RevealOnScroll enableAnimation={isAnimationEnabled}>
                        {isV2 ? (
                            // Figma v2: rounded maroon CTA, Poppins SemiBold 16px (desktop only;
                            // a full-width mobile CTA is rendered below the carousel).
                            <button
                                className="hidden sm:inline-flex items-center justify-center gap-[10px] w-fit px-[24px] py-[16px] rounded-[8px] text-[16px] font-poppins font-semibold cursor-pointer transition-opacity hover:opacity-90"
                                data-cta-name="apply_now"
                                onClick={handleApplyClick}
                                style={{
                                    color: "#ffffff",
                                    backgroundColor: "#9f1c34",
                                    backgroundImage: ctaGradient,
                                    filter: ctaShadow,
                                }}
                            >
                                {parsedData.apply_now_text}
                                {parsedData.apply_now_button_icon && (
                                    <Image
                                        src={parsedData.apply_now_button_icon}
                                        alt={parsedData.apply_now_button_icon_alt || ""}
                                        width={24}
                                        height={24}
                                        className="w-[24px] h-[24px] object-contain shrink-0"
                                    />
                                )}
                            </button>
                        ) : (
                        <button
                            className={cn(
                                // Figma compact CTA: flat px-24 / py-16, 16px text, rounded-8 (no fixed
                                // width, no per-breakpoint upscaling). btn-gradient keeps the gradient bg.
                                "btn-gradient !hidden sm:!inline-flex items-center justify-center gap-[10px] w-fit cursor-pointer",
                                "rounded-[8px] px-[24px] py-[16px] text-[16px] font-semibold",
                            )}
                            data-cta-name="apply_now"
                            onClick={handleApplyClick}
                            style={{
                                fontFamily: "var(--font-google-sans)",
                                backgroundImage: ctaGradient,
                                // Optional soft drop shadow — same hue as the button (Figma maroon fallback).
                                filter: ctaShadow,
                            }}
                        >
                            {parsedData.apply_now_text}
                            {parsedData.apply_now_button_icon && (
                                <Image
                                    src={parsedData.apply_now_button_icon}
                                    alt={parsedData.apply_now_button_icon_alt || ""}
                                    width={24}
                                    height={24}
                                    className="w-[24px] h-[24px] object-contain shrink-0"
                                />
                            )}
                        </button>
                        )}
                        </RevealOnScroll>
                    )}
                </div>

                {/* Right Column - Certificate Carousel */}
                <div className="flex flex-col flex-1 items-center">
                    {/* Carousel Container */}
                    <div 
                        className={cn(
                            "relative w-full",
                            // xs: 255px image + 16px gap + ~24px title = ~295px
                            "h-[295px]",
                            // sm: 303px image + 24px gap + ~26px title = ~353px
                            "sm:h-[353px]",
                            // md: 338px image + 24px gap + ~28px title = ~390px
                            "md:h-[390px]",
                            // lg: 405px image + 32px gap + ~32px title = ~469px
                            "lg:h-[469px]",
                            // xl: 467px image + 32px gap + ~36px title = ~535px
                            "xl:h-[535px]",
                            // Gap between title and navigation slider
                            "sm:mb-[32px]",
                            "md:mb-[32px]",
                            "lg:mb-[40px]",
                            "xl:mb-[40px]"
                        )}
                        onTouchStart={onTouchStart}
                        onTouchMove={onTouchMove}
                        onTouchEnd={onTouchEnd}
                    >
                        <RevealOnScroll scaleStart={0.5} enableAnimation={isAnimationEnabled}>
                        {certificates.map((cert, index) => (
                            <CertificateCard
                                key={cert.id}
                                certificate={cert}
                                position={index - currentIndex}
                                isMobile={isMobile}
                                isV2={isV2}
                            />
                        ))}
                        </RevealOnScroll>
                    </div>

                    {/* Navigation Controls - Figma exact: container, gaps, button sizes, track & moving bar widths */}
                    {
                        certificates.length > 1 && (
                            <RevealOnScroll enableAnimation={isAnimationEnabled}>
                            <div className={cn(
                                "flex items-center shrink-0",
                                // Container width (Figma 598:4194, 826:10971, 826:11023, 822:10905, 826:10931)
                                "w-[327px] h-[67px]",
                                "sm:w-[418px]",
                                "gap-[20px]",
                                "sm:gap-[20px]"
                            )}>
                                {/* Previous Button - exact size per breakpoint */}
                                {parsedData.left_navigation_icon ? (
                                    <button
                                        onClick={handlePrev}
                                        disabled={currentIndex === 0}
                                        className={cn(
                                            "shrink-0 transition-all",
                                            "w-[48px] h-[48px] sm:w-[44px] sm:h-[44px] md:w-[48px] md:h-[48px] lg:w-[56px] lg:h-[56px] xl:w-[64px] xl:h-[64px]",
                                            currentIndex === 0 ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:opacity-80"
                                        )}
                                        aria-label="Previous certificate"
                                    >
                                        <Image 
                                            src={parsedData.left_navigation_icon} 
                                            alt={parsedData.alt_text_left_nav_icon || "Previous"}
                                            width={64}
                                            height={64}
                                            className="w-full h-full object-contain"
                                        />
                                    </button>
                                ) : (
                                    <button
                                        onClick={handlePrev}
                                        disabled={currentIndex === 0}
                                        className={cn(
                                            "flex items-center justify-center rounded-full border-2 transition-all shrink-0",
                                            "w-[48px] h-[48px] sm:w-[44px] sm:h-[44px] md:w-[48px] md:h-[48px] lg:w-[56px] lg:h-[56px] xl:w-[64px] xl:h-[64px]",
                                            currentIndex === 0
                                                ? "border-gray-300 text-gray-300 cursor-not-allowed"
                                                : "border-gray-700 text-gray-700 hover:bg-gray-100 cursor-pointer"
                                        )}
                                        aria-label="Previous certificate"
                                    >
                                        <LeftArrow />
                                    </button>
                                )}

                                <ProgressSlider
                                    currentIndex={currentIndex}
                                    totalCount={certificates.length}
                                    barWidthPx={61}
                                    trackClassName="xs:flex-1 sm:flex-none sm:w-[290px]"
                                />

                                {/* Next Button - exact size per breakpoint */}
                                {parsedData.right_navigation_icon ? (
                                    <button
                                        onClick={handleNext}
                                        disabled={currentIndex === certificates.length - 1}
                                        className={cn(
                                            "shrink-0 transition-all",
                                            "w-[48px] h-[48px] sm:w-[44px] sm:h-[44px] md:w-[48px] md:h-[48px] lg:w-[56px] lg:h-[56px] xl:w-[64px] xl:h-[64px]",
                                            currentIndex === certificates.length - 1 ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:opacity-80"
                                        )}
                                        aria-label="Next certificate"
                                    >
                                        <Image 
                                            src={parsedData.right_navigation_icon} 
                                            alt={parsedData.alt_text_right_nav_icon || "Next"}
                                            width={64}
                                            height={64}
                                            className="w-full h-full object-contain"
                                        />
                                    </button>
                                ) : (
                                    <button
                                        onClick={handleNext}
                                        disabled={currentIndex === certificates.length - 1}
                                        className={cn(
                                            "flex items-center justify-center rounded-full border-2 transition-all shrink-0",
                                            "w-[48px] h-[48px] sm:w-[44px] sm:h-[44px] md:w-[48px] md:h-[48px] lg:w-[56px] lg:h-[56px] xl:w-[64px] xl:h-[64px]",
                                            currentIndex === certificates.length - 1
                                                ? "border-gray-300 text-gray-300 cursor-not-allowed"
                                                : "border-gray-700 text-gray-700 hover:bg-gray-100 cursor-pointer"
                                        )}
                                        aria-label="Next certificate"
                                    >
                                        <RightArrow />
                                    </button>
                                )}
                            </div>
                            </RevealOnScroll>
                        )
                    }
                </div>

                {/* Figma v2: full-width CTA shown only on mobile (below the carousel). */}
                {isV2 && parsedData.apply_now_text && (
                    <RevealOnScroll enableAnimation={isAnimationEnabled}>
                    <button
                        className="sm:hidden flex items-center justify-center gap-[10px] w-full px-[24px] py-[16px] rounded-[8px] text-[16px] font-poppins font-semibold cursor-pointer transition-opacity hover:opacity-90"
                        data-cta-name="apply_now"
                        onClick={handleApplyClick}
                        style={{
                            color: "#ffffff",
                            backgroundColor: "#9f1c34",
                            backgroundImage: ctaGradient,
                            filter: ctaShadow,
                        }}
                    >
                        {parsedData.apply_now_text}
                        {parsedData.apply_now_button_icon && (
                            <Image
                                src={parsedData.apply_now_button_icon}
                                alt={parsedData.apply_now_button_icon_alt || ""}
                                width={24}
                                height={24}
                                className="w-[24px] h-[24px] object-contain shrink-0"
                            />
                        )}
                    </button>
                    </RevealOnScroll>
                )}
            </div>
        </section>
    );
};